import { compact } from "lodash";
import qs from "qs";
import * as R from "ramda";

import {
  ProductKey,
  ProductSpecKey,
  productTypeToPostfix,
  type Product,
  type ProductId,
  type ProductType,
  type StandaloneProductVariant
} from "../specs/product";
import type { Currency } from "../types/currency";
import type { ImageManipulationOptions } from "../types/image-manipulation";
import type { ProductPrintAdvertisement } from "../types/print-mail";
import type { AssortmentPriceSettings, ModelYearDisplay } from "../types/settings";

import { buildServiceUrl, type UrlParams } from "./client-utils";
import { getSingleSpecValue } from "./specs";

export const getProductImageFile = <Type extends ProductType>(
  product: Product<Type> | StandaloneProductVariant<Type>,
  useMainImage = true
) => {
  const mainImageFile = getSingleSpecValue(ProductSpecKey.MainImageFile, product);
  return !useMainImage || R.isNil(mainImageFile)
    ? getSingleSpecValue(ProductSpecKey.ImageFile, product)
    : mainImageFile;
};

export const getProductImageUrl = <Type extends ProductType>(
  product: Product<Type> | StandaloneProductVariant<Type>,
  options: ImageManipulationOptions = {},
  useMainImage = true
) => {
  const productType = product[ProductKey.ProductType];
  const brandKey = getSingleSpecValue(ProductSpecKey.BrandKey, product);
  const postfix = productTypeToPostfix[productType];

  return getPackageUrl(
    `${brandKey}-${postfix}`,
    `images/${getProductImageFile(product, useMainImage)}`,
    compact([
      !!options.width && { param: "width", value: options.width },
      !!options.format && { param: "format", value: options.format }
    ])
  );
};

export const getBrandLogoUrl = (brandKey: string): string => {
  return buildServiceUrl("content", `brands/${brandKey}/logo`).toString();
};

/**
 * Returns the content service path of a content package, or of a file inside it if `filePath` is given.
 * Use it to request package resources via `getFromService("content", …)`.
 */
export const getPackagePath = (packageName: string, filePath: string = ""): string =>
  ["packages", packageName, filePath].filter(Boolean).join("/");

/**
 * Returns the URL of a content package, or of a file inside it if `filePath` is given.
 */
export const getPackageUrl = (packageName: string, filePath?: string, params?: UrlParams): string =>
  buildServiceUrl("content", getPackagePath(packageName, filePath), params).toString();

export const getPrintResourcesFileUrl = (fileName: string) => {
  return buildServiceUrl("print", `/resources/${fileName}`).toString();
};

const getProductPreviewPath =
  (ext: "pdf" | "html") =>
  (
    productId: ProductId,
    bikeCenterName: string,
    showPrices: boolean,
    advertisement: ProductPrintAdvertisement | undefined,
    currency: Currency,
    showAssortmentPrices: boolean = false,
    showFallbackPrices: boolean = false,
    showOriginalPrices: boolean = false,
    useRrpAsOriginalPrice: boolean = true,
    showSaleBadge: AssortmentPriceSettings["showSaleBadge"] = null,
    modelYearDisplayTerm: ModelYearDisplay["term"] = "modelYear",
    concealModelYear: ModelYearDisplay["concealed"] = false,
    variantId?: ProductId
  ): string => {
    const query = {
      bikeCenterName,
      showPrices,
      advertisement,
      currency,
      showAssortmentPrices,
      showFallbackPrices,
      showOriginalPrices,
      useRrpAsOriginalPrice,
      showSaleBadge,
      modelYearDisplayTerm,
      concealModelYear,
      variantId
    };
    const queryString = qs.stringify(query);
    return `product/preview/${encodeURIComponent(productId)}.${ext}?${queryString}`;
  };

export const getProductHtmlPreviewPath = getProductPreviewPath("html");

export const getProductPdfPreviewPath = getProductPreviewPath("pdf");

export const getProductPrintPath = (
  productId: ProductId,
  numCopies: number,
  bikeCenterName: string,
  showPrices: boolean,
  advertisement: ProductPrintAdvertisement | undefined,
  currency: Currency,
  showAssortmentPrices: boolean = false,
  showFallbackPrices: boolean = false,
  showOriginalPrices: boolean = false,
  useRrpAsOriginalPrice: boolean = true,
  showSaleBadge: AssortmentPriceSettings["showSaleBadge"] = null,
  modelYearDisplayTerm: ModelYearDisplay["term"] = "modelYear",
  concealModelYear: ModelYearDisplay["concealed"] = false,
  variantId?: ProductId
): string => {
  const query = {
    bikeCenterName,
    showPrices,
    advertisement,
    currency,
    numCopies,
    showAssortmentPrices,
    showFallbackPrices,
    showOriginalPrices,
    useRrpAsOriginalPrice,
    showSaleBadge,
    modelYearDisplayTerm,
    concealModelYear,
    variantId
  };
  const queryString = qs.stringify(query);
  return `product/${encodeURIComponent(productId)}?${queryString}`;
};
